/**
 * MEDIA API REST client (F3-E), F3-F. Wraps the control-plane
 * endpoints (auth/quota/signatures); the DATA plane (uploading/downloading
 * parts) goes DIRECTLY browser→R2 with the presigned URLs it returns.
 *
 * F3-E contract (fixed even though it isn't in `origin/master` yet):
 *   POST   /api/media/uploads                    → { assetId, uploadId, partSizeBytes }
 *   GET    /api/media/uploads/:id                 → status + parts already uploaded (resume)
 *   POST   /api/media/uploads/:id/part-urls       → [{ partNumber, url }]
 *   POST   /api/media/uploads/:id/complete        → VideoAssetMeta
 *   DELETE /api/media/uploads/:id
 *   POST   /api/media/videos/:id/poster (multipart)
 *   PATCH  /api/media/videos/:id                  → VideoAssetMeta
 *   GET    /api/media/videos?courseId=            → VideoAssetMeta[]
 *   GET    /api/media/videos/:id/play?format=json → { url, expiresAt }
 *   GET    /api/media/videos/:id/poster           → image
 *
 * The transport (`mediaApiFetch`) is INJECTED → testable with a mock, no network.
 */

import type { PlayUrl, VideoAssetMeta } from "@space/shared";
import { mediaApiFetch } from "@/lib/api";

export interface CreateUploadRequest {
  fileName: string;
  sizeBytes: number;
  contentType: string;
  title: string;
  courseId?: string | null;
  lessonId?: string | null;
  classSessionId?: string | null;
}

export interface CreateUploadResponse {
  assetId: string;
  uploadId: string;
  partSizeBytes: number;
}

export interface UploadStatusPart {
  partNumber: number;
  etag: string;
}

export interface UploadStatusResponse {
  assetId: string;
  uploadId: string;
  partSizeBytes: number;
  sizeBytes: number;
  status: string;
  uploadedParts: UploadStatusPart[];
}

export interface PartUrl {
  partNumber: number;
  url: string;
}

export type MediaFetch = (path: string, init?: RequestInit) => Promise<Response>;

/** Media API error with the HTTP status (to distinguish 413 quota, etc.). */
export class MediaApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "MediaApiError";
  }
}

/** Builds a diagnostic `MediaApiError` (internal message, not UI). */
function httpError(context: string, status: number, body = ""): MediaApiError {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- internal transport diagnostics, not UI text
  return new MediaApiError(`${context}: HTTP ${status} ${body}`.trim(), status);
}

async function readJson<T>(res: Response, context: string): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw httpError(context, res.status, body);
  }
  // Trust-boundary read of the server JSON; callers validate what they use.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (await res.json()) as T;
}

export interface MediaApi {
  createUpload(req: CreateUploadRequest): Promise<CreateUploadResponse>;
  getUpload(uploadId: string): Promise<UploadStatusResponse>;
  partUrls(uploadId: string, partNumbers: number[]): Promise<PartUrl[]>;
  completeUpload(uploadId: string, parts: UploadStatusPart[]): Promise<VideoAssetMeta>;
  abortUpload(uploadId: string): Promise<void>;
  uploadPoster(videoId: string, poster: Blob): Promise<void>;
  renameVideo(videoId: string, title: string): Promise<VideoAssetMeta>;
  listVideos(courseId?: string | null): Promise<VideoAssetMeta[]>;
  deleteVideo(videoId: string): Promise<void>;
  getPlayUrl(videoId: string): Promise<PlayUrl>;
  /** Poster's absolute URL (use directly as `<img src>` / `poster`). */
  posterUrl(videoId: string): string;
}

export function createMediaApi(fetchImpl: MediaFetch = mediaApiFetch): MediaApi {
  return {
    async createUpload(req) {
      const res = await fetchImpl("/api/media/uploads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
      });
      return readJson<CreateUploadResponse>(res, "createUpload");
    },

    async getUpload(uploadId) {
      const res = await fetchImpl(`/api/media/uploads/${encodeURIComponent(uploadId)}`);
      return readJson<UploadStatusResponse>(res, "getUpload");
    },

    async partUrls(uploadId, partNumbers) {
      const res = await fetchImpl(`/api/media/uploads/${encodeURIComponent(uploadId)}/part-urls`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ partNumbers }),
      });
      return readJson<PartUrl[]>(res, "partUrls");
    },

    async completeUpload(uploadId, parts) {
      const res = await fetchImpl(`/api/media/uploads/${encodeURIComponent(uploadId)}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parts }),
      });
      return readJson<VideoAssetMeta>(res, "completeUpload");
    },

    async abortUpload(uploadId) {
      const res = await fetchImpl(`/api/media/uploads/${encodeURIComponent(uploadId)}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 404) {
        throw httpError("abortUpload", res.status);
      }
    },

    async uploadPoster(videoId, poster) {
      const form = new FormData();
      form.append("file", poster, "poster.jpg");
      const res = await fetchImpl(`/api/media/videos/${encodeURIComponent(videoId)}/poster`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        throw httpError("uploadPoster", res.status);
      }
    },

    async renameVideo(videoId, title) {
      const res = await fetchImpl(`/api/media/videos/${encodeURIComponent(videoId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      return readJson<VideoAssetMeta>(res, "renameVideo");
    },

    async listVideos(courseId) {
      const res = await fetchImpl(
        courseId
          ? `/api/media/videos?courseId=${encodeURIComponent(courseId)}`
          : "/api/media/videos",
      );
      return readJson<VideoAssetMeta[]>(res, "listVideos");
    },

    async deleteVideo(videoId) {
      const res = await fetchImpl(`/api/media/videos/${encodeURIComponent(videoId)}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 404) {
        throw httpError("deleteVideo", res.status);
      }
    },

    async getPlayUrl(videoId) {
      const res = await fetchImpl(
        `/api/media/videos/${encodeURIComponent(videoId)}/play?format=json`,
      );
      return readJson<PlayUrl>(res, "getPlayUrl");
    },

    posterUrl(videoId) {
      return `/api/media/videos/${encodeURIComponent(videoId)}/poster`;
    },
  };
}

/** Default instance (real transport). */
export const mediaApi: MediaApi = createMediaApi();
